import { apiUrl } from '../config';

type Options = RequestInit & { raw?: boolean; headers?: HeadersInit; publicRoute?: boolean };
function api<T>(url: string, options: Options): Promise<T> {
  // Check if this is a public route (like signin/signup)
  const isPublicRoute = options?.publicRoute !== undefined ? options.publicRoute : false;

  // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
  const isFormData = options?.body instanceof FormData;

  // Get default headers, but exclude Content-Type if FormData
  const defaultHeaders = authHeader(isPublicRoute);
  let headersToUse: HeadersInit;

  if (isFormData) {
    // For FormData, remove Content-Type from default headers
    const { 'Content-Type': _, ...headersWithoutContentType } = defaultHeaders as Record<string, string>;
    headersToUse = headersWithoutContentType;
  } else {
    headersToUse = defaultHeaders;
  }

  // If custom headers are provided, merge them (custom headers take precedence)
  const mergedHeaders = options?.headers
    ? { ...headersToUse, ...(options.headers as Record<string, string>) }
    : headersToUse;

  return fetch(url, { ...options, headers: mergedHeaders }).then(
    async (response) => {
      if (!response.ok) {
        throw new Error(JSON.stringify(await response.json()));
      }
      if (options?.raw) return response as unknown as Promise<T>;
      return response.json() as Promise<T>;
    }
  );
}

function get<T>(url, options?: Options) {
  return api<T>(apiUrl + url, options);
}

function post<T>(url, data, options?: Options, isNotJson?: boolean) {
  return api<T>(apiUrl + url, {
    ...options,
    method: 'POST',
    body: isNotJson ? data : JSON.stringify(data)
  });
}

function patch<T>(url, data, options?: Options) {
  return api<T>(apiUrl + url, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data)
  });
}

function deletes<T>(url, options?: Options) {
  return api<T>(apiUrl + url, { ...options, method: 'DELETE' });
}

export function authHeader(publicRoute: boolean): HeadersInit {
  // return authorization header with jwt token
  let accessToken = localStorage.getItem('accessToken');

  if (!publicRoute && accessToken) {
    return {
      Authorization: 'Bearer ' + accessToken,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true'
    };
  } else {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true'
    };
  }
}

export default { get, patch, post, deletes };
